import React, { useCallback, useEffect, useState, useRef } from 'react';
import { formatDateCST, formatDateForInput, getTodayCST } from '../../utils/dateUtils';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Factory as FactoryIcon,
  Biotech as BiotechIcon,
  VerifiedUser as VerifiedIcon,
  Link as LinkIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useOrg } from '../../contexts/OrgContext';

// New tab components
import ManufacturedTab from '../../components/donor/ManufacturedTab';
import QCTestingTab from '../../components/donor/QCTestingTab';
import ProductReleaseTab from '../../components/donor/ProductReleaseTab';
import CompletedTab from '../../components/donor/CompletedTab';
import FinalizeRawMaterialsButton from '../../components/donor/FinalizeRawMaterialsButton';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatDateOnly = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Group raw materials by item description AND lot number (different lots = separate rows)
const groupByItemAndLot = (usageArray) => {
  if (!usageArray || usageArray.length === 0) return [];
  
  const grouped = {};
  usageArray.forEach((usage) => {
    // Create unique key from item description + lot number
    const itemDesc = usage.itemDescription || 'Unknown Item';
    const lotNum = usage.lotNumber || 'N/A';
    const key = `${itemDesc}|||${lotNum}`;
    
    if (!grouped[key]) {
      grouped[key] = {
        itemDescription: itemDesc,
        vendor: usage.vendor || '-',
        lotNumber: lotNum,
        dates: [],
        usages: [],
        totalQuantity: 0
      };
    }
    
    // Add date with quantity for this usage
    const dateStr = formatDateForInput(usage.dateUsed);
    if (dateStr) {
      // Store date with quantity for better display
      const existingDateEntry = grouped[key].dates.find(d => d.date === dateStr);
      if (existingDateEntry) {
        existingDateEntry.quantity += usage.quantityUsed || 0;
      } else {
        grouped[key].dates.push({
          date: dateStr,
          quantity: usage.quantityUsed || 0
        });
      }
    }
    
    grouped[key].usages.push(usage);
    grouped[key].totalQuantity += usage.quantityUsed || 0;
  });
  
  // Sort dates for each item
  Object.values(grouped).forEach(item => {
    item.dates.sort((a, b) => new Date(a.date) - new Date(b.date));
  });
  
  // Convert to array and sort by item description, then lot number
  return Object.values(grouped).sort((a, b) => {
    const itemCompare = a.itemDescription.localeCompare(b.itemDescription);
    if (itemCompare !== 0) return itemCompare;
    return a.lotNumber.localeCompare(b.lotNumber);
  });
};

const DonorDetailPage = ({ currentCompany }) => {
  const navigate = useNavigate();
  const { donorId } = useParams();
  const { org: contextOrg } = useOrg();

  const effectiveOrg = (currentCompany || contextOrg || 'epl').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [donor, setDonor] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [certificateUploading, setCertificateUploading] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [dragOverDocType, setDragOverDocType] = useState(null);
  const fileInputRefs = useRef({});
  
  // Raw materials dialog state
  const [addMaterialDialogOpen, setAddMaterialDialogOpen] = useState(false);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [materialForm, setMaterialForm] = useState({
    rawMaterialReceivingId: '',
    quantityUsed: '',
    dateUsed: getTodayCST(),
    notes: ''
  });
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Status management state
  const [statusForm, setStatusForm] = useState({
    status: '',
    notes: ''
  });

  const loadDonor = useCallback(async () => {
    if (!donorId) return;
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Donor not found');
      }

      const data = await response.json();
      const donorData = data?.data || data;

      if (!donorData) {
        throw new Error('Donor not found');
      }

      // Calculate total vials from manufacturedVials array
      const totalVials = (donorData.manufacturedVials || []).reduce((sum, vial) => sum + (vial.quantity || 0), 0);
      
      setDonor({
        ...donorData,
        tissues: donorData.tissues || [],
        eligibilityDocuments: donorData.eligibilityDocuments || [],
        manufacturedVials: donorData.manufacturedVials || [],
        rawMaterialUsage: donorData.rawMaterialUsage || [],
        calculatedTotalVials: totalVials
      });
      
      // Initialize status form with current status
      setStatusForm({
        status: donorData.status || '',
        notes: ''
      });
    } catch (err) {
      console.error('❌ Failed to load donor detail:', err);
      setError(err?.message || 'Unable to load donor');
    } finally {
      setLoading(false);
    }
  }, [donorId]);

  useEffect(() => {
    loadDonor();
  }, [loadDonor]);

  // Fetch available raw materials for the add dialog
  const fetchAvailableMaterials = async () => {
    try {
      setMaterialsLoading(true);
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/raw-materials/approved?organization=EPL`;
      console.log('Fetching raw materials from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Raw materials response:', data);
        
        // Get available quantities for each receiving
        const materialsWithAvailability = await Promise.all(
          (data.data || []).map(async (receiving) => {
            const usageResponse = await fetch(`${API_BASE_URL}/donors/${donorId}/raw-materials`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            let totalUsed = 0;
            if (usageResponse.ok) {
              const usageData = await usageResponse.json();
              totalUsed = (usageData.data || [])
                .filter(u => u.rawMaterialReceivingId === receiving.id)
                .reduce((sum, u) => sum + (u.quantityUsed || 0), 0);
            }
            
            const available = (receiving.qtyConforming || receiving.qtyReceived) - totalUsed;
            
            // Check if material is expired
            let isExpired = false;
            let expirationDate = null;
            if (!receiving.expirationDateNA && receiving.expirationDate) {
              expirationDate = new Date(receiving.expirationDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              expirationDate.setHours(0, 0, 0, 0);
              isExpired = expirationDate < today;
            }
            
            return {
              ...receiving,
              availableQuantity: Math.max(0, available),
              isExpired,
              expirationDate: receiving.expirationDate
            };
          })
        );
        
        // Filter out expired materials and those with no available quantity
        const filtered = materialsWithAvailability.filter(m => m.availableQuantity > 0 && !m.isExpired);
        console.log('Available materials after filtering:', filtered);
        setAvailableMaterials(filtered);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch raw materials:', response.status, response.statusText, errorText);
        setError(`Failed to load raw materials: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching available materials:', err);
    } finally {
      setMaterialsLoading(false);
    }
  };

  const handleOpenAddMaterial = () => {
    setMaterialForm({
      rawMaterialReceivingId: '',
      quantityUsed: '',
      dateUsed: new Date().toISOString().split('T')[0],
      notes: ''
    });
    fetchAvailableMaterials();
    setAddMaterialDialogOpen(true);
  };

  const handleAddMaterial = async () => {
    if (!materialForm.rawMaterialReceivingId || !materialForm.quantityUsed) {
      setError('Please select a material and enter quantity');
      return;
    }

    const selectedMaterial = availableMaterials.find(m => m.id === materialForm.rawMaterialReceivingId);
    if (!selectedMaterial) {
      setError('Selected material not found');
      return;
    }

    // Check if material is expired (frontend validation)
    if (selectedMaterial.isExpired) {
      const expiredDate = selectedMaterial.expirationDate 
        ? formatDateCST(selectedMaterial.expirationDate)
        : 'unknown date';
      setError(`Cannot use expired raw material. This material expired on ${expiredDate}.`);
      return;
    }

    if (parseInt(materialForm.quantityUsed) > selectedMaterial.availableQuantity) {
      setError(`Cannot use ${materialForm.quantityUsed} units. Only ${selectedMaterial.availableQuantity} available.`);
      return;
    }

    setUpdating(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donorId}/raw-materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rawMaterialReceivingId: materialForm.rawMaterialReceivingId,
          quantityUsed: parseInt(materialForm.quantityUsed),
          dateUsed: materialForm.dateUsed,
          notes: materialForm.notes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add raw material usage');
      }

      setSuccessMessage('Raw material usage recorded successfully');
      setAddMaterialDialogOpen(false);
      await loadDonor();
    } catch (err) {
      console.error('Error adding raw material:', err);
      setError(err?.message || 'Failed to add raw material usage');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusForm.status) {
      setError('Please select a status');
      return;
    }

    if (statusForm.status === donor.status) {
      setError('Status is already set to this value');
      return;
    }

    setUpdating(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donorId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: statusForm.status,
          notes: statusForm.notes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update status');
      }

      setSuccessMessage('Status updated successfully');
      setStatusForm({ status: '', notes: '' });
      await loadDonor();
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteMaterial = async (usageId) => {
    if (!window.confirm('Are you sure you want to remove this raw material usage? This will restore the inventory.')) {
      return;
    }

    setUpdating(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donorId}/raw-materials/${usageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove raw material usage');
      }

      setSuccessMessage('Raw material usage removed successfully');
      await loadDonor();
    } catch (err) {
      console.error('Error removing raw material:', err);
      setError(err?.message || 'Failed to remove raw material usage');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileSelect = (documentType) => {
    if (fileInputRefs.current[documentType]) {
      fileInputRefs.current[documentType].click();
    }
  };

  const handleDocumentUpload = async (documentType, eventOrFile) => {
    // Support both file input events and direct file objects (from drag-and-drop)
    const file = eventOrFile?.target?.files?.[0] || eventOrFile;
    if (!file || !donor) return;

    // Accept PDF, DOC, DOCX, and image files
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF, DOC, DOCX, JPG, and PNG files are supported.');
      if (eventOrFile?.target) {
        eventOrFile.target.value = '';
      }
      return;
    }

    setUploadingDocType(documentType);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/donors/${donorId}/documents/${documentType}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload document');
      }

      setSuccessMessage(`${data.data?.fileName || 'Document'} uploaded successfully.`);
      await loadDonor();
    } catch (err) {
      console.error('❌ Failed to upload document:', err);
      setError(err?.message || 'Unable to upload document.');
    } finally {
      if (fileInputRefs.current[documentType]) {
        fileInputRefs.current[documentType].value = '';
      } else if (eventOrFile?.target) {
        eventOrFile.target.value = '';
      }
      setUploadingDocType(null);
    }
  };

  // Drag and drop handlers for document upload
  const handleDragOver = (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocType(documentType);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocType(null);
  };

  const handleDrop = async (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocType(null);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await handleDocumentUpload(documentType, files[0]);
    }
  };

  const handleRefresh = async () => {
    await loadDonor();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!donor) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to locate this donor.
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
          Back
        </Button>
      </Box>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', path: `/internal/${effectiveOrg}/dashboard` },
    { label: 'Donor Management', path: `/internal/${effectiveOrg}/donor-management` },
    { label: donor.donorId || 'Donor Detail' }
  ];

  return (
    <Box sx={{ pb: 6 }}>
      <Breadcrumbs items={breadcrumbItems} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/internal/${effectiveOrg}/donor-management`)}
          >
            Back to Donor Management
          </Button>
          <Chip
            icon={<CheckCircleIcon />}
            color={donor.status === 'RELEASED' ? 'success' : donor.status === 'ELIGIBLE' ? 'success' : 'warning'}
            label={`Status: ${donor.status}`}
            sx={{ fontWeight: 600 }}
          />
          {donor.tissueTypeReceived && (
            <Chip
              label={`Tissue: ${donor.tissueTypeReceived}`}
              color="info"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          {donor.linkedDonor && (
            <Chip
              icon={<LinkIcon />}
              label={`Linked: ${donor.linkedDonor.donorId}`}
              color="secondary"
              size="small"
              component={Link}
              to={`/internal/epl/donor-management/${donor.linkedDonor.id}`}
              clickable
              sx={{ fontWeight: 600 }}
            />
          )}
        </Stack>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 12px 30px rgba(3,149,130,0.12)' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Donor
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <InventoryIcon sx={{ color: '#027A6B' }} />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {donor.donorId}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {donor.mareName}
                </Typography>
                <Typography variant="body2">
                  Date Received: <strong>{formatDate(donor.dateReceived)}</strong>
                </Typography>
                {/* Prominent Vial Count Display */}
                {(() => {
                  const totalVials = (donor.manufacturedVials || []).reduce((sum, vial) => sum + (vial.quantity || 0), 0);
                  const qcSamplesUsed = (donor?.qcSamples || [])
                    .filter(s => s.sampleType === 'STERILITY' || s.sampleType === 'ENDOTOXIN')
                    .reduce((sum, s) => sum + (s.quantity || 0), 0);
                  const availableVials = totalVials - qcSamplesUsed;
                  
                  if (totalVials > 0) {
                    return (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: '#dcfce7', borderRadius: 1, border: '1px solid #22c55e' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a' }}>
                          Vials Made: <strong style={{ fontSize: '1.1em' }}>{availableVials}</strong>
                        </Typography>
                        {qcSamplesUsed > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            ({totalVials} manufactured - {qcSamplesUsed} for QC testing)
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Timeline
                </Typography>
                <Typography variant="body2">
                  Received: <strong>{formatDate(donor.dateReceived)}</strong>
                </Typography>
                {donor.processingStartedAt && (
                  <Typography variant="body2">
                    Processing Started: <strong>{formatDate(donor.processingStartedAt)}</strong>
                  </Typography>
                )}
                {donor.manufacturingCompletedAt && (
                  <Typography variant="body2">
                    Manufacturing: <strong>{formatDate(donor.manufacturingCompletedAt)}</strong>
                  </Typography>
                )}
                {donor.releasedAt && (
                  <Typography variant="body2">
                    Released: <strong>{formatDate(donor.releasedAt)}</strong>
                  </Typography>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Summary
                </Typography>
                {/* Calculate total vials from manufacturedVials array */}
                {(() => {
                  const totalVials = (donor.manufacturedVials || []).reduce((sum, vial) => sum + (vial.quantity || 0), 0);
                  const vialBreakdown = (donor.manufacturedVials || []).reduce((acc, vial) => {
                    const key = vial.productName || vial.productSku || 'Unknown';
                    acc[key] = (acc[key] || 0) + (vial.quantity || 0);
                    return acc;
                  }, {});
                  
                  return (
                    <>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: totalVials > 0 ? '#dcfce7' : '#f3f4f6', 
                        borderRadius: 2, 
                        border: totalVials > 0 ? '2px solid #22c55e' : '1px solid #e5e7eb'
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Total Vials Made
                        </Typography>
                        {(() => {
                          const qcSamplesUsed = (donor?.qcSamples || [])
                            .filter(s => s.sampleType === 'STERILITY' || s.sampleType === 'ENDOTOXIN')
                            .reduce((sum, s) => sum + (s.quantity || 0), 0);
                          const availableVials = totalVials - qcSamplesUsed;
                          return (
                            <>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: totalVials > 0 ? '#16a34a' : 'text.secondary' }}>
                                {availableVials}
                              </Typography>
                              {qcSamplesUsed > 0 && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                  ({totalVials} manufactured - {qcSamplesUsed} for QC testing)
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                        {totalVials > 0 && Object.keys(vialBreakdown).length > 0 && (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                              Breakdown:
                            </Typography>
                            {Object.entries(vialBreakdown).map(([product, qty]) => (
                              <Typography key={product} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                • {product}: <strong>{qty}</strong>
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Typography variant="body2">
                        Tissues: <strong>{(donor.tissues || []).length}</strong>
                      </Typography>
                      {donor.wetIcePresent !== undefined && (
                        <Typography variant="body2">
                          Wet Ice Present: <strong>{donor.wetIcePresent ? 'Yes' : 'No'}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2">
                        Documents: <strong>{(donor.eligibilityDocuments || []).filter(d => d.status === 'UPLOADED' || d.status === 'APPROVED').length} / {(donor.eligibilityDocuments || []).length}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Raw Materials Used: <strong>{(donor.rawMaterialUsage || []).length}</strong>
                      </Typography>
                    </>
                  );
                })()}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Donor Details
            </Typography>
            <Tabs
              value={activeTab}
              onChange={(_, value) => {
                setActiveTab(value);
              }}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PdfIcon fontSize="small" />
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      Recovery Documentation
                    </Typography>
                    <Chip
                      size="small"
                      color="primary"
                      label={`${(donor.eligibilityDocuments || []).filter(d => d.status === 'UPLOADED' || d.status === 'APPROVED').length}/${(donor.eligibilityDocuments || []).length || 10}`}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ScienceIcon fontSize="small" />
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      Raw Materials
                    </Typography>
                    <Chip
                      size="small"
                      color={donor.rawMaterialsFinalized ? 'success' : 'secondary'}
                      label={donor.rawMaterialsFinalized ? '✓' : (donor.rawMaterialUsage || []).length}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FactoryIcon fontSize="small" />
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      Manufactured
                    </Typography>
                    <Chip
                      size="small"
                      color="info"
                      label={(donor.manufacturedVials || []).reduce((sum, v) => sum + (v.quantity || 0), 0)}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BiotechIcon fontSize="small" />
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      QC Testing
                    </Typography>
                    <Chip
                      size="small"
                      color={donor.qcTestingComplete ? 'success' : 'warning'}
                      label={donor.qcTestingComplete ? '✓' : (donor.qcSamples || []).length}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                }
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <VerifiedIcon fontSize="small" />
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      Product Release
                    </Typography>
                    {donor.status === 'RELEASED' && (
                      <Chip
                        size="small"
                        color="success"
                        label="✓"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                }
              />
            </Tabs>

            <Divider />

            {activeTab === 0 && (
              <Box>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Eligibility Documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CloudUploadIcon fontSize="small" />
                      Drag & drop files onto rows to upload
                    </Typography>
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Document Type</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>File Name</strong></TableCell>
                          <TableCell><strong>Uploaded</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(donor.eligibilityDocuments || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No documents uploaded yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (donor.eligibilityDocuments || []).map((doc) => (
                            <TableRow 
                              key={doc.id}
                              onDragOver={(e) => handleDragOver(e, doc.documentType)}
                              onDragEnter={(e) => handleDragOver(e, doc.documentType)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, doc.documentType)}
                              sx={{
                                transition: 'all 0.2s ease',
                                ...(dragOverDocType === doc.documentType && {
                                  backgroundColor: 'primary.light',
                                  opacity: 0.9,
                                  '& td': {
                                    borderTop: '2px dashed',
                                    borderBottom: '2px dashed',
                                    borderColor: 'primary.main',
                                  },
                                  '& td:first-of-type': {
                                    borderLeft: '2px dashed',
                                    borderColor: 'primary.main',
                                  },
                                  '& td:last-of-type': {
                                    borderRight: '2px dashed',
                                    borderColor: 'primary.main',
                                  }
                                })
                              }}
                            >
                              <TableCell>{doc.documentName}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={doc.status}
                                  color={doc.status === 'UPLOADED' || doc.status === 'APPROVED' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>{doc.fileName || '-'}</TableCell>
                              <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {doc.documentUrl && (
                                    <Tooltip title={doc.fileName ? `View ${doc.fileName}` : 'View document (if you see an error, the file may have been deleted - use Replace to re-upload)'}>
                                      <Button
                                        size="small"
                                        startIcon={<PdfIcon />}
                                        onClick={() => {
                                          // Open document in new tab
                                          // If document doesn't exist, Azure will show ResourceNotFound error
                                          // User can then use Replace button to re-upload
                                          window.open(doc.documentUrl, '_blank', 'noopener,noreferrer');
                                        }}
                                        variant="outlined"
                                      >
                                        View
                                      </Button>
                                    </Tooltip>
                                  )}
                                  {uploadingDocType === doc.documentType ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <>
                                      <input
                                        type="file"
                                        ref={(el) => fileInputRefs.current[doc.documentType] = el}
                                        style={{ display: 'none' }}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={(e) => handleDocumentUpload(doc.documentType, e)}
                                      />
                                      <Button
                                        size="small"
                                        variant={doc.documentUrl ? 'outlined' : 'contained'}
                                        startIcon={<CloudUploadIcon />}
                                        onClick={() => handleFileSelect(doc.documentType)}
                                        disabled={!!uploadingDocType}
                                      >
                                        {doc.documentUrl ? 'Replace' : 'Upload'}
                                      </Button>
                                    </>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Raw Materials Used
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddMaterial}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Raw Material
                    </Button>
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="40px"></TableCell>
                          <TableCell><strong>Item Description</strong></TableCell>
                          <TableCell><strong>Lot Number</strong></TableCell>
                          <TableCell><strong>Vendor</strong></TableCell>
                          <TableCell><strong>Total Quantity</strong></TableCell>
                          <TableCell><strong>Dates Used</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(donor.rawMaterialUsage || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No raw materials used yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          groupByItemAndLot(donor.rawMaterialUsage || []).map((group, groupIndex) => {
                            const rowKey = `${group.itemDescription}-${group.lotNumber}-${groupIndex}`;
                            const isExpanded = expandedRows[rowKey] || false;
                            
                            // Format dates summary
                            const datesSummary = group.dates.length === 0 
                              ? '-' 
                              : group.dates.length === 1
                              ? formatDateOnly(group.dates[0].date)
                              : `${group.dates.length} dates`;
                            
                            return (
                              <React.Fragment key={rowKey}>
                                {/* Summary Row */}
                                <TableRow 
                                  sx={{ 
                                    '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' },
                                    bgcolor: isExpanded ? 'action.selected' : 'transparent'
                                  }}
                                  onClick={() => setExpandedRows(prev => ({
                                    ...prev,
                                    [rowKey]: !prev[rowKey]
                                  }))}
                                >
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedRows(prev => ({
                                          ...prev,
                                          [rowKey]: !prev[rowKey]
                                        }));
                                      }}
                                    >
                                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {group.itemDescription}
                                  </TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {group.lotNumber}
                                  </TableCell>
                                  <TableCell>{group.vendor}</TableCell>
                                  <TableCell>
                                    <strong>{group.totalQuantity}</strong>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {datesSummary}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Expanded Details Row */}
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                          Usage Details
                                        </Typography>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell><strong>Date Used</strong></TableCell>
                                              <TableCell><strong>Quantity</strong></TableCell>
                                              <TableCell><strong>Used By</strong></TableCell>
                                              <TableCell><strong>Notes</strong></TableCell>
                                              <TableCell align="right"><strong>Actions</strong></TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {group.usages.map((usage) => (
                                              <TableRow key={usage.id} hover>
                                                <TableCell>
                                                  {formatDateOnly(usage.dateUsed)}
                                                </TableCell>
                                                <TableCell>
                                                  <strong>{usage.quantityUsed}</strong>
                                                </TableCell>
                                                <TableCell>
                                                  {usage.usedByName || '-'}
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="body2" color="text.secondary">
                                                    {usage.notes || '-'}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteMaterial(usage.id)}
                                                    disabled={updating}
                                                    title={`Delete usage on ${formatDateOnly(usage.dateUsed)}`}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Finalize Raw Materials Button */}
                  <Divider sx={{ mt: 3 }} />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <FinalizeRawMaterialsButton 
                      donor={donor} 
                      onFinalize={loadDonor}
                    />
                  </Box>
                </Stack>
              </Box>
            )}

            {/* Manufactured Tab */}
            {activeTab === 2 && (
              <ManufacturedTab donor={donor} onRefresh={loadDonor} />
            )}

            {/* QC Testing Tab */}
            {activeTab === 3 && (
              <QCTestingTab donor={donor} onRefresh={loadDonor} />
            )}

            {/* Product Release Tab */}
            {activeTab === 4 && (
              <ProductReleaseTab donor={donor} onRefresh={loadDonor} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Add Raw Material Dialog */}
      <Dialog open={addMaterialDialogOpen} onClose={() => setAddMaterialDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Raw Material Usage</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Raw Material *</InputLabel>
              <Select
                value={materialForm.rawMaterialReceivingId}
                onChange={(e) => {
                  const selected = availableMaterials.find(m => m.id === e.target.value);
                  setMaterialForm({
                    ...materialForm,
                    rawMaterialReceivingId: e.target.value,
                    quantityUsed: selected?.availableQuantity || ''
                  });
                }}
                label="Raw Material *"
                disabled={materialsLoading}
              >
                {materialsLoading ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : availableMaterials.length === 0 ? (
                  <MenuItem disabled>No approved raw materials available</MenuItem>
                ) : (
                  availableMaterials.map((material) => {
                    const isExpired = material.isExpired;
                    const expirationDate = material.expirationDate 
                      ? formatDateCST(material.expirationDate)
                      : null;
                    
                    return (
                      <MenuItem key={material.id} value={material.id} disabled={isExpired}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Box sx={{ flex: 1 }}>
                            {material.itemDescription} - Lot: {material.lotNumber || 'N/A'} (Available: {material.availableQuantity})
                            {expirationDate && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                Expires: {expirationDate}
                              </Typography>
                            )}
                          </Box>
                          {isExpired && (
                            <Chip 
                              label="EXPIRED" 
                              size="small" 
                              color="error" 
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Quantity Used *"
              type="number"
              value={materialForm.quantityUsed}
              onChange={(e) => setMaterialForm({ ...materialForm, quantityUsed: e.target.value })}
              inputProps={{ min: 1 }}
            />

            <TextField
              fullWidth
              label="Date Used *"
              type="date"
              value={materialForm.dateUsed}
              onChange={(e) => setMaterialForm({ ...materialForm, dateUsed: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={materialForm.notes}
              onChange={(e) => setMaterialForm({ ...materialForm, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMaterialDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleAddMaterial}
            variant="contained"
            disabled={updating || !materialForm.rawMaterialReceivingId || !materialForm.quantityUsed}
          >
            {updating ? 'Adding...' : 'Add Usage'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DonorDetailPage;

